import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

export const generatePDF = async (data, setPdfData) => {
  try {
    if (!data) return;
    
    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage([612, 792]);
    
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const boldItalicFont = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
    
    // Full PDF generation logic starts here
    const cleanText = (text) => {
    if (!text) return '';
    return text.replace(/[\n\r\t\v\f]/g, ' ');
    };

    const textColor = rgb(0, 0, 0);
    const marginLeft = 40;
    const marginRight = 40;
    const marginTop = 32;
    const marginBottom = 32;
    const pageWidth = 612;
    const pageHeight = 792;
    const contentWidth = pageWidth - marginLeft - marginRight;
    let currentY = pageHeight - marginTop - 15;
    const lineHeightMultiplier = 1.21;
    const bulletLineHeightMultiplier = 1.21;
    const bulletSpacingMultiplier = 1.4;

    const toTitleCase = (text) => {
    if (!text) return '';
    return text
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const TITLE_FONT_SIZE = 15.73;
    const NORMAL_FONT_SIZE = 9.333;

    const roundToTenth = (num) => {
    return Math.round(num * 10) / 10;
    };

    const drawText = (text, x, y, options = {}, targetPage = page) => {
    const size = options.size || NORMAL_FONT_SIZE;
    const defaultOptions = { 
        size: size, 
        font: font, 
        color: textColor,
        lineHeight: roundToTenth(size * lineHeightMultiplier)
    };
    const mergedOptions = { ...defaultOptions, ...options };
    targetPage.drawText(text, { 
        x, 
        y, 
        ...mergedOptions 
    });
    return mergedOptions.lineHeight;
    };

    const drawSectionHeader = (text, y, targetPage = page) => {
    const lineHeight = roundToTenth(NORMAL_FONT_SIZE * lineHeightMultiplier);
    drawText(text.toUpperCase(), marginLeft, y, {
        font: boldFont,
        size: NORMAL_FONT_SIZE
    }, targetPage);
    
    targetPage.drawRectangle({
        x: marginLeft,
        y: y - 5,
        width: pageWidth - marginLeft - marginRight,
        height: 1.25,
        borderRadius: 0.625,
        color: rgb(0, 0, 0)
    }, targetPage);
    
    return y - lineHeight - 10;
    };

    const checkForNewPage = () => {
    if (currentY < marginBottom + 32) {
        page = pdfDoc.addPage([612, 792]);
        currentY = pageHeight - marginTop - 15;
        return true;
    }
    return false;
    };

    // Header
    const fullName = `${cleanText(data.first_name || '')} ${cleanText(data.last_name || '')}`;
    let headerY = currentY;
    const nameWidth = boldFont.widthOfTextAtSize(fullName, TITLE_FONT_SIZE);
    drawText(fullName, (pageWidth - nameWidth) / 2, headerY, {
    font: boldFont,
    size: TITLE_FONT_SIZE
    });

    headerY = headerY - TITLE_FONT_SIZE;

    const phoneText = cleanText(data.phone || '');
    const emailText = cleanText(data.email || '');
    const linkedinText = cleanText(data.linkedin_link || '');
    
    if (phoneText || emailText || linkedinText) {
    let contactParts = [];
    
    // If linkedin is available: email | phone | linkedin
    // If no linkedin: phone | email
    if (linkedinText) {
        if (emailText) contactParts.push(emailText);
        if (phoneText) contactParts.push(phoneText);
        contactParts.push(linkedinText);
    } else {
        if (phoneText) contactParts.push(phoneText);
        if (emailText) contactParts.push(emailText);
    }
    
    const contactText = contactParts.join(' | ');
    
    const contactWidth = font.widthOfTextAtSize(contactText, NORMAL_FONT_SIZE);
    drawText(contactText, (pageWidth - contactWidth) / 2, headerY, {
        size: NORMAL_FONT_SIZE
    });
    }

    const contactLineHeight = roundToTenth(NORMAL_FONT_SIZE * lineHeightMultiplier);
    currentY = headerY - contactLineHeight - 16;
    
    // Professional Summary
    if (data.professional_summary) {
    checkForNewPage();
    currentY = drawSectionHeader('Professional Summary', currentY);
    
    const summaryText = cleanText(data.professional_summary);
    const firstSentenceMatch = summaryText.match(/^[^.!?]*[.!?]/);
    const firstSentence = firstSentenceMatch ? firstSentenceMatch[0] : '';
    
    let summaryY = currentY;
    const summaryLineHeight = roundToTenth(NORMAL_FONT_SIZE * lineHeightMultiplier);
    
    const words = summaryText.split(' ');
    const firstSentenceWordCount = firstSentence.split(' ').length;
    
    let lines = [];
    let currentLine = '';
    let currentLineWidth = 0;
    let currentWordCount = 0;
    
    for (const word of words) {
        currentWordCount++;
        const isFirstSentence = currentWordCount <= firstSentenceWordCount;
        const fontToUse = isFirstSentence ? boldItalicFont || boldFont : font;
        
        try {
        const wordWidth = fontToUse.widthOfTextAtSize(word, NORMAL_FONT_SIZE);
        const spaceWidth = currentLine ? fontToUse.widthOfTextAtSize(' ', NORMAL_FONT_SIZE) : 0;
        
        if (currentLineWidth + spaceWidth + wordWidth > contentWidth && currentLine) {
            lines.push({
            text: currentLine,
            endWordCount: currentWordCount - 1
            });
            
            currentLine = word;
            currentLineWidth = wordWidth;
        } else {
            currentLine = currentLine ? currentLine + ' ' + word : word;
            currentLineWidth += spaceWidth + wordWidth;
        }
        } catch (e) {
        console.warn("Error measuring word width:", e);
        
        if (currentLine.length > 60) {
            lines.push({
            text: currentLine,
            endWordCount: currentWordCount - 1
            });
            currentLine = word;
        } else {
            currentLine = currentLine ? currentLine + ' ' + word : word;
        }
        }
    }
    
    if (currentLine) {
        lines.push({
        text: currentLine,
        endWordCount: currentWordCount
        });
    }
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineWords = line.text.split(' ');
        let xPos = marginLeft;
        
        let wordNumber = line.endWordCount - lineWords.length + 1;
        
        for (const word of lineWords) {
        const isFirstSentence = wordNumber <= firstSentenceWordCount;
        const fontToUse = isFirstSentence ? boldItalicFont || boldFont : font;
        
        try {
            drawText(word, xPos, summaryY, {
            size: NORMAL_FONT_SIZE,
            font: fontToUse
            });
            
            const wordWidth = fontToUse.widthOfTextAtSize(word, NORMAL_FONT_SIZE);
            const spaceWidth = fontToUse.widthOfTextAtSize(' ', NORMAL_FONT_SIZE);
            xPos += wordWidth + spaceWidth;
        } catch (e) {
            console.warn("Error drawing word:", e);
            
            try {
            drawText(word, xPos, summaryY, {
                size: NORMAL_FONT_SIZE,
                font: font
            });
            
            xPos += word.length * 6 + 3;
            } catch (fallbackError) {
            console.error("Even fallback drawing failed:", fallbackError);
            xPos += word.length * 6 + 3;
            }
        }
        
        wordNumber++;
        }
        
        summaryY -= summaryLineHeight;
    }
    
    currentY = summaryY - 12;
    }
    
    // Work Experience
    if (data.companies && data.companies.length > 0) {
    checkForNewPage();
    currentY = drawSectionHeader('Work Experience', currentY);
    
    for (let companyIndex = 0; companyIndex < data.companies.length; companyIndex++) {
        const company = data.companies[companyIndex];
        if (!company.name) continue;
        
        checkForNewPage();
        
        const companyText = cleanText(company.name);
        const locationText = cleanText(company.location || '');
        
        drawText(companyText, marginLeft, currentY);
        
        if (locationText) {
        const locationWidth = font.widthOfTextAtSize(locationText, NORMAL_FONT_SIZE);
        drawText(locationText, pageWidth - marginRight - locationWidth, currentY);
        }
        
        currentY -= 16;
        
        if (company.roles && company.roles.length > 0) {
        for (let roleIndex = 0; roleIndex < company.roles.length; roleIndex++) {
            const role = company.roles[roleIndex];
            if (!role.title) continue;
            
            checkForNewPage();
            
            const roleText = cleanText(role.title);
            const yearsText = `${cleanText(role.start_year || '')}-${cleanText(role.end_year || '')}`;
            
            drawText(roleText, marginLeft, currentY, { font: boldFont });
            
            const yearsWidth = boldFont.widthOfTextAtSize(yearsText, NORMAL_FONT_SIZE); 
            drawText(yearsText, pageWidth - marginRight - yearsWidth, currentY, { font: boldFont });
            
            currentY -= 16;
            
            if (role.bullets && role.bullets.length > 0) {
            for (const bulletRaw of role.bullets) {
                if (!bulletRaw || !bulletRaw.trim()) continue;
                
                checkForNewPage();
                
                const bullet = cleanText(bulletRaw);
                
                drawText('•', marginLeft + 5, currentY);
                
                const bulletTextWidth = contentWidth - 15;
                
                const words = bullet.split(' ');
                let line = '';
                let bulletY = currentY;
                
                for (const word of words) {
                const testLine = line + (line ? ' ' : '') + word;
                
                try {
                    const width = font.widthOfTextAtSize(testLine, NORMAL_FONT_SIZE);
                    
                    if (width > bulletTextWidth && line.length > 0) {
                    drawText(line, marginLeft + 15, bulletY);
                    line = word;
                    bulletY -= roundToTenth(NORMAL_FONT_SIZE * bulletLineHeightMultiplier);
                    } else {
                    line = testLine;
                    }
                } catch (e) {
                    console.warn('Error measuring text width:', e);
                    drawText(line || word, marginLeft + 15, bulletY);
                    line = '';
                    bulletY -= roundToTenth(NORMAL_FONT_SIZE * bulletLineHeightMultiplier);
                }
                }
                
                if (line.length > 0) {
                drawText(line, marginLeft + 15, bulletY);
                bulletY -= roundToTenth(NORMAL_FONT_SIZE * bulletSpacingMultiplier);
                }
                
                currentY = bulletY;
            }
            }
            
            const hasMoreRoles = company.roles.slice(roleIndex + 1).some(nextRole => nextRole.title);
            
            if (hasMoreRoles) {
            currentY -= 10;
            }
        }
        }
        
        const hasMoreCompanies = data.companies.slice(companyIndex + 1).some(nextCompany => 
        nextCompany.name
        );
        
        if (hasMoreCompanies) {
        currentY -= 10;
        }
    }
    
    currentY -= 12;
    }
    
    // Projects
    if (data.projects && data.projects.length > 0) {
    checkForNewPage();
    currentY = drawSectionHeader('Projects', currentY);
    
    for (let projectIndex = 0; projectIndex < data.projects.length; projectIndex++) {
        const project = data.projects[projectIndex];
        if (!project.name) continue;
        
        checkForNewPage();
        
        const projectText = cleanText(project.name);
        const githubText = cleanText(project.github_link || '');
        
        drawText(projectText, marginLeft, currentY, { font: boldFont });
        
        if (githubText) {
        const githubWidth = font.widthOfTextAtSize(githubText, NORMAL_FONT_SIZE);
        drawText(githubText, pageWidth - marginRight - githubWidth, currentY);
        }
        
        currentY -= 16;
        
        if (project.description) {
        const description = cleanText(project.description);
        
        drawText('•', marginLeft + 5, currentY);
        
        const bulletTextWidth = contentWidth - 15;
        
        const words = description.split(' ');
        let line = '';
        let bulletY = currentY;
        
        for (const word of words) {
            const testLine = line + (line ? ' ' : '') + word;
            
            try {
            const width = font.widthOfTextAtSize(testLine, NORMAL_FONT_SIZE);
            
            if (width > bulletTextWidth && line.length > 0) {
                drawText(line, marginLeft + 15, bulletY);
                line = word;
                bulletY -= roundToTenth(NORMAL_FONT_SIZE * bulletLineHeightMultiplier);
            } else {
                line = testLine;
            }
            } catch (e) {
            console.warn('Error measuring text width:', e);
            drawText(line || word, marginLeft + 15, bulletY);
            line = '';
            bulletY -= roundToTenth(NORMAL_FONT_SIZE * bulletLineHeightMultiplier);
            }
        }
        
        if (line.length > 0) {
            drawText(line, marginLeft + 15, bulletY);
            bulletY -= roundToTenth(NORMAL_FONT_SIZE * lineHeightMultiplier);
        }
        
        currentY = bulletY;
        }
        
        const hasMoreProjects = data.projects.slice(projectIndex + 1).some(nextProject => 
        nextProject.name
        );
        
        if (hasMoreProjects) {
        currentY -= 10;
        }
    }
    
    currentY -= 12;
    }
    
    // Education
    if (data.education && data.education.length > 0) {
    checkForNewPage();
    currentY = drawSectionHeader('Education', currentY);
    
    for (let i = 0; i < data.education.length; i++) {
        const edu = data.education[i];
        if (!edu.degree || !edu.school) continue;
        
        checkForNewPage();
        
        drawText(cleanText(edu.degree), marginLeft, currentY, { font: boldFont });
        
        if (edu.year) {
        const yearWidth = font.widthOfTextAtSize(cleanText(edu.year), NORMAL_FONT_SIZE);
        drawText(cleanText(edu.year), pageWidth - marginRight - yearWidth, currentY);
        }
        
        currentY -= 14 + 1;
        
        const schoolText = cleanText(edu.school);
        const gpaText = edu.gpa ? `GPA: ${cleanText(edu.gpa)}` : '';
        
        drawText(schoolText, marginLeft, currentY);
        
        if (gpaText) {
        const gpaWidth = font.widthOfTextAtSize(gpaText, NORMAL_FONT_SIZE);
        drawText(gpaText, pageWidth - marginRight - gpaWidth, currentY);
        }
        
        const hasMoreEntries = data.education.slice(i + 1).some(nextEdu => 
        nextEdu.degree && nextEdu.school
        );
        
        if (hasMoreEntries) {
        currentY -= 20;
        } else {
        currentY -= 12;
        }
    }
    
    currentY -= 12;
    }
    
    // Skills
    if (data.programming_languages || data.applications || data.technical_skills || data.non_technical_skills || data.domain_experience || data.skills) {
    checkForNewPage();
    currentY = drawSectionHeader('Relevant Skills', currentY);
    
    const drawWrappedSkill = (label, skillText, y) => {
        if (!skillText) return y;
        
        drawText(label, marginLeft, y, { font: boldFont });
        
        const labelWidth = boldFont.widthOfTextAtSize(label, NORMAL_FONT_SIZE);
        const textX = marginLeft + labelWidth + 3;
        
        const firstLineAvailableWidth = contentWidth - labelWidth - 3;
        const continuationAvailableWidth = contentWidth;
        
        const words = cleanText(skillText).split(' ');
        let line = '';
        let lineY = y;
        
        let isFirstLine = true;
        
        for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const testLine = line ? line + ' ' + word : word;
        
        try {
            const testWidth = font.widthOfTextAtSize(testLine, NORMAL_FONT_SIZE);
            const currentAvailableWidth = isFirstLine ? firstLineAvailableWidth : continuationAvailableWidth;
            
            if (testWidth > currentAvailableWidth && line) {
            drawText(line, isFirstLine ? textX : marginLeft, lineY);
            
            line = word;
            lineY -= (NORMAL_FONT_SIZE * lineHeightMultiplier);
            isFirstLine = false;
            } else {
            line = testLine;
            }
        } catch (e) {
            console.warn('Error measuring text width:', e);
            line = testLine;
        }
        }
        
        if (line) {
        drawText(line, isFirstLine ? textX : marginLeft, lineY);
        lineY -= (NORMAL_FONT_SIZE + 4);
        }
        
        return lineY;
    };
    
    if (data.is_tech_job) {
        // Tech job skills
        if (data.programming_languages) {
        checkForNewPage();
        currentY = drawWrappedSkill('Programming Languages:', data.programming_languages, currentY);
        currentY -= 4;
        }
        
        if (data.applications) {
        checkForNewPage();
        currentY = drawWrappedSkill('Applications:', data.applications, currentY);
        currentY -= 4;
        }
        
        if (data.technical_skills) {
        checkForNewPage();
        currentY = drawWrappedSkill('Technical Skills:', data.technical_skills, currentY);
        }

        if (data.non_technical_skills) {
            checkForNewPage();
            currentY = drawWrappedSkill('Soft Skills:', data.non_technical_skills, currentY);
            currentY -= 4;
        }

    } else if (!data.is_tech_job && (data.technical_skills || data.non_technical_skills || data.domain_experience)) {
        // Non-tech job skills
        if (data.technical_skills) {
        checkForNewPage();
        currentY = drawWrappedSkill('Technical Skills:', data.technical_skills, currentY);
        currentY -= 4;
        }
        
        if (data.non_technical_skills) {
        checkForNewPage();
        currentY = drawWrappedSkill('Soft Skills:', data.non_technical_skills, currentY);
        currentY -= 4;
        }
        
        if (data.domain_experience) {
        checkForNewPage();
        currentY = drawWrappedSkill('Domain Experience:', data.domain_experience, currentY);
        }
    } else if (data.skills) {
        checkForNewPage();
        currentY = drawWrappedSkill('Skills:', data.skills, currentY);
    }
    
    currentY -= 12;
    }
    // End of PDF generation logic
    
    const pdfBytes = await pdfDoc.save();
    setPdfData(btoa(String.fromCharCode(...pdfBytes)));
    return true;
  } catch (error) {
    console.error("Error generating PDF:", error);
    return false;
  }
};